import Seo from "@/components/Seo";
import Header from "@/components/landing/Header";
import IndustryStrip from "@/components/landing/IndustryStrip";
import LeadForm from "@/components/landing/LeadForm";
import Footer from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { scrollToId } from "@/lib/scroll";
import { ArrowRight, PhoneOff, DollarSign, Voicemail } from "lucide-react";

const articles = [
  {
    icon: PhoneOff,
    id: "missed-calls",
    title: "How many calls does a contractor actually miss?",
    body: [
      "More than most owners think. Calls arrive while techs are on roofs, under houses, and driving between jobs — and the office can only answer one line at a time. Industry patterns consistently show that a large share of service calls land outside business hours or during peak workload windows, exactly when nobody can pick up.",
      "The painful part isn't the ring itself — it's what happens next. The overwhelming majority of callers who can't reach a business don't leave a voicemail and don't call back. They tap the next result on Google. For a contractor, a ringing phone that rolls to voicemail is functionally the same as handing the job to a competitor.",
      "The fix isn't hiring another dispatcher to sit by the phone. It's making sure every missed call gets an instant, useful response — which is precisely what missed-call text-back systems are built for.",
    ],
    takeaway: "Run your own numbers with the leak calculator on our homepage.",
  },
  {
    icon: DollarSign,
    id: "true-cost",
    title: "The real cost of a missed emergency call",
    body: [
      "Start with the obvious number: the job itself. A missed AC-repair call in July or a burst pipe at 9 PM carries a ticket anywhere from a few hundred dollars to several thousand — and emergency work is usually the highest-margin work a contractor does.",
      "Then the hidden number: lifetime value. The homeowner whose emergency you handle becomes your maintenance customer, your replacement customer, and your referral source. Lose the first call and you typically lose the whole relationship — often to the competitor who simply answered faster.",
      "Finally, the marketing number: you already paid for that call. Ads, Google Business Profile, yard signs, and referrals all cost money to generate. A missed call is a marketing dollar with zero return.",
    ],
    takeaway: "One recovered emergency job usually covers a month of Backfill.",
  },
  {
    icon: Voicemail,
    id: "voicemail",
    title: "Why voicemail fails in 2026",
    body: [
      "Caller behavior has changed. People under 50 overwhelmingly text first and treat voicemail as a dead end — many never listen to their own voicemail, let alone leave one for a business. When the phone rings out, the modern caller's next step is the next search result, not a message.",
      "Speed compounds the problem. In home services, the first business to respond wins the job the majority of the time. A voicemail returned two hours later isn't a recovery — it's a courtesy call to a customer who already booked someone else.",
      "Texting meets callers where they already are. An instant, branded text after a missed call keeps the conversation alive in the channel people actually answer, and it works at 2 PM and 2 AM equally.",
    ],
    takeaway: "Try it on your own phone — the homepage has a live demo.",
  },
];

export default function Resources() {
  return (
    <>
      <Seo
        title="Resources for Contractors — Backfill"
        description="Short, practical reads on missed-call revenue loss for HVAC, plumbing, electrical, roofing and general contracting businesses."
      />
      <Header />
      <IndustryStrip />
      <main>
        <section data-testid="resources-hero" className="noise relative overflow-hidden pt-20 pb-16 sm:pt-24">
          <div className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]" />
          <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
            <Reveal>
              <Eyebrow>Resources</Eyebrow>
              <h1 className="mt-5 font-display text-4xl font-bold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl">
                The cost of the call you didn't answer
              </h1>
              <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
                Short, practical reads for contractors on missed calls, lost
                revenue, and what actually fixes them.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="pb-24">
          <div className="mx-auto max-w-3xl space-y-8 px-5 sm:px-8">
            {articles.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.06}>
                <article
                  id={a.id}
                  data-testid={`article-${a.id}`}
                  className="rounded-xl border border-white/[0.08] bg-[#12161F] p-7 sm:p-9"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md border border-[#F25F22]/30 bg-[#F25F22]/10">
                      <a.icon className="h-5 w-5 text-[#F25F22]" />
                    </div>
                    <h2 className="font-display text-lg font-bold text-slate-50 sm:text-xl">
                      {a.title}
                    </h2>
                  </div>
                  <div className="mt-5 space-y-4">
                    {a.body.map((p, j) => (
                      <p key={j} className="text-sm leading-relaxed text-slate-400 sm:text-base">
                        {p}
                      </p>
                    ))}
                  </div>
                  <p className="mt-6 flex items-center gap-2 border-t border-white/[0.06] pt-5 text-sm font-medium text-[#F25F22]">
                    <ArrowRight className="h-4 w-4" />
                    {a.takeaway}
                  </p>
                </article>
              </Reveal>
            ))}

            <Reveal delay={0.1} className="text-center">
              <a
                href="#demo"
                data-testid="resources-demo-cta"
                onClick={(e) => { e.preventDefault(); scrollToId("#demo"); }}
                className="group inline-flex items-center gap-2 rounded-md bg-[#F25F22] px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#FF7539] hover:shadow-[0_0_28px_rgba(242,95,34,0.45)]"
              >
                Stop Losing Calls — Book a Demo
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </a>
            </Reveal>
          </div>
        </section>

        <LeadForm source="resources" />
      </main>
      <Footer />
    </>
  );
}
